// "What's new" is hosted once, in containers/App, so the dialog and its tour
// survive the route changes the tour makes. Anything else (the ? menu in the
// top nav, the keyboard shortcut, the console command) opens it through this
// window event rather than holding a reference to the host. Same pattern as
// RUN_AI_READINESS_EVENT in Setup/AiReadinessSection.
export const OPEN_WHATS_NEW_EVENT = 'tripadmit:open-whats-new';

// Fired by the host whenever the number of unseen entries changes, so the ?
// button can show its dot without fetching the catalogue a second time. The
// last value is kept here too: the top nav remounts on every page and has to
// pick it up without waiting for the next change.
export const WHATS_NEW_COUNT_EVENT = 'tripadmit:whats-new-count';

let lastCount = 0;

export const getWhatsNewCount = () => lastCount;

export const publishWhatsNewCount = count => {
  lastCount = count || 0;
  window.dispatchEvent(new CustomEvent(WHATS_NEW_COUNT_EVENT, { detail: lastCount }));
};

// options: { mode: 'new' | 'all', since: 'YYYY-MM-DD' }
export const openWhatsNew = (options = {}) =>
  window.dispatchEvent(new CustomEvent(OPEN_WHATS_NEW_EVENT, { detail: options }));

// Typing a "?" into a form field must not pop a dialog over it.
export const isTypingTarget = target => {
  if (!target || !target.tagName) return false;
  const tag = target.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || !!target.isContentEditable;
};

// "?" is Shift+/ on most layouts; checking `key` rather than the code keeps it
// working on layouts where ? lives elsewhere.
export const isWhatsNewShortcut = event =>
  event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey && !isTypingTarget(event.target);

// The automatic popup only belongs on the signed-in dashboard, never on the
// public booking widgets an operator may have open in the same browser.
const AUTO_OPEN_PREFIXES = ['/tipdirect', '/taptotip', '/settings', '/messaging'];

export const isAutoOpenPath = pathname => {
  const path = (pathname || '').toLowerCase();
  return AUTO_OPEN_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
};

// Another first-run screen that must not be covered: the guide's "Which tours
// do you guide?" picker (components/TourSelectionGate). It announces itself
// with a body attribute and a window event; read here by name rather than by
// importing it, so neither feature depends on the other being present.
export const TOUR_SELECTION_ATTR = 'data-tour-selection-open';
export const TOUR_SELECTION_EVENT = 'tourselection:change';

export const isTourSelectionOpen = () =>
  typeof document !== 'undefined' && document.body.getAttribute(TOUR_SELECTION_ATTR) === 'true';

// And the company admin's "Jobs to do" (components/AdminJobs), which takes
// priority: while it is still checking for jobs ('pending') or open, the
// popup waits, and it opens once the jobs dialog is closed. Same contract as
// the tour picker, read by name for the same reason.
export const ADMIN_JOBS_ATTR = 'data-admin-jobs';
export const ADMIN_JOBS_EVENT = 'adminjobs:change';

export const isAdminJobsBlocking = () => {
  if (typeof document === 'undefined') return false;
  const state = document.body.getAttribute(ADMIN_JOBS_ATTR);
  return state === 'pending' || state === 'open';
};

// And the company admin's Product Improvement credits notice
// (components/PipCredits, card 457), which comes after the jobs and before
// this. Same contract again.
export const PIP_CREDITS_ATTR = 'data-pip-credits';
export const PIP_CREDITS_EVENT = 'pipcredits:change';

export const isPipCreditsBlocking = () => {
  if (typeof document === 'undefined') return false;
  const state = document.body.getAttribute(PIP_CREDITS_ATTR);
  return state === 'pending' || state === 'open';
};

/** Another first-run screen has the floor; an automatic popup waits. */
export const isSomethingElseFirst = () => isTourSelectionOpen() || isAdminJobsBlocking() || isPipCreditsBlocking();

// Someone still being walked through onboarding has never used the product:
// there is no "last visit" to report on, and the popup would bury the form.
// Same test as checkOnboardingFlow in components/Routes (loose `==` there too,
// so a missing flag does not count as incomplete).
export const hasIncompleteOnboarding = user =>
  // eslint-disable-next-line eqeqeq
  user?.profile?.onboarding?.tipping == false || user?.profile?.onboarding?.profile == false;
